import { createSelector } from '@reduxjs/toolkit'
import { pipe, propEq } from 'ramda'
import { IGenericResource } from 'api-client/common-types'
import { allKey } from 'app/constants'
import createSorter, { SortConfig } from 'core/helpers/createSorter'
import getDataSelector from 'core/utils/getDataSelector'
import { createSharedSelector, selectParamsFromProps } from 'core/utils/selectorHelpers'
import DataKeys from 'k8s/DataKeys'
import { findCluster } from 'k8s/util/helpers'
import { arrayIfEmpty, filterIf } from 'utils/fp'
import { durationBetweenDates } from 'utils/misc'
import { ConfigMapItem } from './models'

export enum IConfigMapsTabs {
  Overview = 'overview',
  Yaml = 'yaml',
}

export interface IConfigMapSelector extends IGenericResource<ConfigMapItem> {
  clusterName: string
  clusterId: string
  dataSize: number
  ownerReferences: ConfigMapItem['metadata']['ownerReferences']
  age?: string
  labels: ConfigMapItem['metadata']['labels']
  annotations: ConfigMapItem['metadata']['annotations']
}

export const configMapsSelector = createSharedSelector(
  getDataSelector<DataKeys.ConfigMaps>(DataKeys.ConfigMaps),
  getDataSelector<DataKeys.KaapiClusters>(DataKeys.KaapiClusters),
  (rawConfigMaps, allClusters): IConfigMapSelector[] => {
    return rawConfigMaps.map((rawConfigMap) => {
      const { clusterId, metadata, data, binaryData } = rawConfigMap
      const clusterData = findCluster(allClusters, clusterId)
      const dataLength = !!data ? Object.keys(data).length : 0
      const binaryDataLength = !!binaryData ? Object.keys(binaryData).length : 0
      const dataSize = dataLength + binaryDataLength
      return {
        ...rawConfigMap,
        clusterName: clusterData?.metadata?.name,
        clusterId,
        dataSize,
        age: durationBetweenDates({ labels: ['d'] })(metadata?.creationTimestamp),
        ownerReferences: metadata?.ownerReferences,
        creationTimestamp: metadata?.creationTimestamp,
        labels: metadata?.labels,
        annotations: metadata?.annotations,
      }
    })
  },
)

export const makeConfigMapsSelector = (
  defaultParams = {} as SortConfig & { namespace?: string; clusterId?: string },
) => {
  const selectParams = selectParamsFromProps(defaultParams)
  return createSelector(
    getDataSelector<DataKeys.ConfigMaps>(DataKeys.ConfigMaps),
    selectParams,
    (configMaps, params) => {
      const { clusterId, namespace, orderBy, orderDirection } = params
      return pipe<any, any, any, any, any>(
        filterIf(clusterId && clusterId !== allKey, propEq('clusterId', clusterId)),
        filterIf(namespace && namespace !== allKey, propEq('namespace', namespace)),
        createSorter({ orderBy, orderDirection }),
        arrayIfEmpty,
      )(configMaps)
    },
  )
}
